import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { IconEdit, IconEye, IconTrash } from "../components/icons";
import { PasswordField } from "../components/PasswordField";
import { formatKathmanduWhen } from "../lib/nepal-time";
import { usePageRefresh } from "../lib/page-lifecycle";
import {
  PASSWORD_MIN_LENGTH,
  type ApiKeyRow,
  type Developer,
  type DeveloperStatus,
  devApi,
  passwordRequirementText,
  validatePassword,
} from "../developer/api";

interface AdminDeveloper extends Developer {
  cameras?: number;
  keys_active?: number;
  disabled_at?: string | null;
  has_password?: boolean;
}

interface AdminDevice {
  id: string;
  phone: string;
  serial_no: string;
  imei: string;
  sim_type: string;
  label: string | null;
  is_active: boolean;
  owner_developer_id: string | null;
  owner_name?: string | null;
  owner_email?: string | null;
  created_at: string | null;
  updated_at: string | null;
  online?: boolean;
  lat?: number | null;
  lon?: number | null;
}

type Tab = "overview" | "cameras" | "keys";
type ModalMode = "create" | "edit" | "password" | null;

function statusLabel(status: DeveloperStatus | string): string {
  if (status === "pending_setup") return "Pending setup";
  if (status === "disabled") return "Inactive";
  if (status === "active") return "Active";
  return status;
}

function roleLabel(role: string): string {
  return role === "superadmin" ? "Super Admin" : "User";
}

function cameraTitle(d: AdminDevice): string {
  return d.label || d.serial_no || d.imei;
}

export function UsersAdminPage({ me }: { me: Developer }) {
  const [developers, setDevelopers] = useState<AdminDeveloper[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{
    developer: AdminDeveloper;
    cameras: number;
    keys: { total: number; active: number };
  } | null>(null);
  const [devices, setDevices] = useState<AdminDevice[]>([]);
  const [allDevices, setAllDevices] = useState<AdminDevice[]>([]);
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [tab, setTab] = useState<Tab>("overview");
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<ModalMode>(null);
  const [secretOnce, setSecretOnce] = useState<string | null>(null);
  const [pendingDisable, setPendingDisable] = useState<AdminDeveloper | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminDeveloper | null>(null);
  const [assignDeviceId, setAssignDeviceId] = useState("");
  const [query, setQuery] = useState("");

  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formConfirm, setFormConfirm] = useState("");
  const [formStatus, setFormStatus] = useState<"active" | "disabled">("disabled");

  const loadList = useCallback(async () => {
    const data = await devApi<{ developers: AdminDeveloper[] }>("/api/admin/developers");
    setDevelopers(data.developers);
  }, []);

  const loadAllDevices = useCallback(async () => {
    const data = await devApi<{ devices: AdminDevice[] }>("/api/admin/devices");
    setAllDevices(data.devices);
  }, []);

  const loadDetail = useCallback(async (id: string) => {
    const [meta, cams, keyData] = await Promise.all([
      devApi<{ developer: AdminDeveloper; cameras: number; keys: { total: number; active: number } }>(
        `/api/admin/developers/${id}`,
      ),
      devApi<{ devices: AdminDevice[] }>(`/api/admin/developers/${id}/devices`),
      devApi<{ keys: ApiKeyRow[] }>(`/api/admin/developers/${id}/keys`),
    ]);
    setDetail(meta);
    setDevices(cams.devices);
    setKeys(keyData.keys);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      await Promise.all([loadList(), loadAllDevices()]);
      if (selectedId) await loadDetail(selectedId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    }
  }, [loadAllDevices, loadDetail, loadList, selectedId]);

  usePageRefresh(refresh);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDevices([]);
      setKeys([]);
      return;
    }
    void loadDetail(selectedId).catch((err) => {
      setError(err instanceof Error ? err.message : "Failed to load user");
    });
  }, [loadDetail, selectedId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return developers;
    return developers.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        d.email.toLowerCase().includes(q) ||
        (d.company ?? "").toLowerCase().includes(q),
    );
  }, [developers, query]);

  const assignable = useMemo(() => {
    if (!selectedId) return [];
    return allDevices.filter((d) => d.owner_developer_id !== selectedId);
  }, [allDevices, selectedId]);

  const openCreate = () => {
    setFormName("");
    setFormEmail("");
    setFormPassword("");
    setFormConfirm("");
    setFormStatus("disabled");
    setModal("create");
    setError(null);
  };

  const openEdit = (user: AdminDeveloper) => {
    setSelectedId(user.id);
    setFormName(user.name);
    setFormEmail(user.email);
    setFormPassword("");
    setFormConfirm("");
    setFormStatus(user.status === "active" ? "active" : "disabled");
    setModal("edit");
    setError(null);
  };

  const openPassword = (user: AdminDeveloper) => {
    setSelectedId(user.id);
    setFormPassword("");
    setFormConfirm("");
    setModal("password");
    setError(null);
  };

  const createUser = async () => {
    const pwErr = validatePassword(formPassword);
    if (pwErr) {
      setError(pwErr);
      return;
    }
    if (formPassword !== formConfirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const data = await devApi<{ developer: AdminDeveloper }>("/api/admin/developers", {
        method: "POST",
        body: JSON.stringify({
          name: formName.trim(),
          email: formEmail.trim(),
          password: formPassword,
          confirm_password: formConfirm,
          status: formStatus,
        }),
      });
      setModal(null);
      setOkMsg(`User ${data.developer.name} created (${statusLabel(data.developer.status)}).`);
      await loadList();
      setSelectedId(data.developer.id);
      setTab("overview");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!selectedId) return;
    const editing = developers.find((d) => d.id === selectedId);
    const nextPassword = formPassword.trim();
    if (nextPassword) {
      const pwErr = validatePassword(nextPassword);
      if (pwErr) {
        setError(pwErr);
        return;
      }
      if (nextPassword !== formConfirm.trim()) {
        setError("Passwords do not match.");
        return;
      }
    } else if (formConfirm.trim()) {
      setError("Enter the new password in both fields, or leave both blank to keep the current password.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, string> = {
        name: formName.trim(),
        email: formEmail.trim(),
      };
      // Never send status for Super Admin — backend rejects real status changes, and
      // omitting avoids accidental 403s when only name/email/password are edited.
      if (editing?.role !== "superadmin") {
        body.status = formStatus;
      }
      if (nextPassword) {
        body.password = nextPassword;
        body.confirm_password = formConfirm.trim();
      }
      const data = await devApi<{ developer: AdminDeveloper }>(`/api/admin/developers/${selectedId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      setModal(null);
      setFormPassword("");
      setFormConfirm("");
      setOkMsg(
        nextPassword
          ? `User updated (status: ${statusLabel(data.developer.status)}). Password changed; sessions revoked.`
          : `User updated (status: ${statusLabel(data.developer.status)}).`,
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async () => {
    if (!selectedId) return;
    const pwErr = validatePassword(formPassword);
    if (pwErr) {
      setError(pwErr);
      return;
    }
    if (formPassword !== formConfirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await devApi(`/api/admin/developers/${selectedId}/password`, {
        method: "POST",
        body: JSON.stringify({ password: formPassword, confirm_password: formConfirm }),
      });
      setModal(null);
      setOkMsg("Password updated. Existing sessions were revoked.");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Password update failed");
    } finally {
      setBusy(false);
    }
  };

  const setEnabled = async (user: AdminDeveloper, enable: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await devApi(`/api/admin/developers/${user.id}/${enable ? "enable" : "disable"}`, { method: "POST" });
      setOkMsg(enable ? "User activated." : "User deactivated. Login blocked.");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setBusy(false);
      setPendingDisable(null);
    }
  };

  const deleteUser = async (user: AdminDeveloper) => {
    setBusy(true);
    setError(null);
    try {
      const data = await devApi<{ cameras_unassigned?: number }>(`/api/admin/developers/${user.id}`, {
        method: "DELETE",
      });
      if (selectedId === user.id) setSelectedId(null);
      setOkMsg(
        `User removed. ${data.cameras_unassigned ?? 0} camera(s) moved to unassigned.`,
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusy(false);
      setPendingDelete(null);
    }
  };

  const assignCamera = async () => {
    if (!selectedId || !assignDeviceId) return;
    setBusy(true);
    setError(null);
    try {
      const data = await devApi<{ devices: AdminDevice[] }>(`/api/admin/developers/${selectedId}/devices/assign`, {
        method: "POST",
        body: JSON.stringify({ device_id: assignDeviceId }),
      });
      setDevices(data.devices);
      setAssignDeviceId("");
      setOkMsg("Camera assigned.");
      await Promise.all([loadList(), loadAllDevices()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Assign failed");
    } finally {
      setBusy(false);
    }
  };

  const unassignCamera = async (deviceId: string) => {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      const data = await devApi<{ devices: AdminDevice[] }>(`/api/admin/developers/${selectedId}/devices/unassign`, {
        method: "POST",
        body: JSON.stringify({ device_id: deviceId }),
      });
      setDevices(data.devices);
      setOkMsg("Camera unassigned.");
      await Promise.all([loadList(), loadAllDevices()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unassign failed");
    } finally {
      setBusy(false);
    }
  };

  const generateKey = async () => {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      const data = await devApi<{ plaintext: string }>(`/api/admin/developers/${selectedId}/keys`, {
        method: "POST",
        body: JSON.stringify({
          name: "Platform key",
          environment: "live",
          scopes: [
            "devices:read",
            "devices:write",
            "streams:read",
            "streams:control",
            "talk:write",
            "positions:read",
            "alarms:read",
            "gps:live",
          ],
        }),
      });
      setSecretOnce(data.plaintext);
      await loadDetail(selectedId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Key create failed");
    } finally {
      setBusy(false);
    }
  };

  const keyAction = async (keyId: string, action: "rotate" | "disable" | "enable" | "revoke") => {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      const data = await devApi<{ plaintext?: string }>(`/api/admin/developers/${selectedId}/keys/${keyId}/${action}`, {
        method: "POST",
      });
      if (data.plaintext) setSecretOnce(data.plaintext);
      await loadDetail(selectedId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Key action failed");
    } finally {
      setBusy(false);
    }
  };

  if (me.role !== "superadmin") {
    return (
      <div className="admin-users-page">
        <div className="banner">Super Admin access required.</div>
      </div>
    );
  }

  return (
    <div className="admin-users-page">
      <div className="admin-users-header">
        <div>
          <h2>Users</h2>
          <p className="muted">Create users, set passwords, assign cameras, and manage API keys.</p>
        </div>
        <div className="admin-users-actions">
          <input
            className="admin-search"
            placeholder="Search users…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search users"
          />
          <button type="button" className="primary" onClick={openCreate}>
            + Create User
          </button>
        </div>
      </div>

      {error ? <div className="banner">{error}</div> : null}
      {okMsg ? (
        <div className="admin-ok-banner">
          {okMsg}
          <button type="button" className="ghost" onClick={() => setOkMsg(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      {secretOnce ? (
        <div className="devportal-secret">
          <strong>Secret (shown once)</strong>
          <code>{secretOnce}</code>
          <button type="button" onClick={() => void navigator.clipboard.writeText(secretOnce)}>
            Copy
          </button>
          <button type="button" className="ghost" onClick={() => setSecretOnce(null)}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="admin-table-wrap panel-block">
        <table className="admin-users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Cameras</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => {
              const isSuper = user.role === "superadmin";
              const selected = selectedId === user.id;
              return (
                <tr key={user.id} className={selected ? "is-selected" : undefined}>
                  <td>
                    <button type="button" className="linkish" onClick={() => { setSelectedId(user.id); setTab("overview"); }}>
                      {user.name}
                    </button>
                  </td>
                  <td>{user.email}</td>
                  <td>{roleLabel(user.role)}</td>
                  <td>{user.cameras ?? 0}</td>
                  <td>
                    <span className={`pill status-${user.status}`}>{statusLabel(user.status)}</span>
                  </td>
                  <td className="muted">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</td>
                  <td>
                    <div className="admin-row-actions">
                      <button
                        type="button"
                        className="icon-btn"
                        title="View"
                        aria-label="View"
                        onClick={() => { setSelectedId(user.id); setTab("overview"); }}
                      >
                        <IconEye />
                      </button>
                      <button
                        type="button"
                        className="icon-btn"
                        title="Edit"
                        aria-label="Edit"
                        onClick={() => openEdit(user)}
                      >
                        <IconEdit />
                      </button>
                      {!isSuper ? (
                        <button
                          type="button"
                          className="icon-btn is-danger"
                          title="Delete"
                          aria-label="Delete"
                          onClick={() => setPendingDelete(user)}
                        >
                          <IconTrash />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted">
                  No users found
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {detail ? (
        <section className="admin-users-detail panel-block">
          <div className="admin-detail-head">
            <div>
              <h3>{detail.developer.name}</h3>
              <p className="muted">
                {detail.developer.email} · {roleLabel(detail.developer.role)} · {statusLabel(detail.developer.status)}
              </p>
            </div>
            <div className="admin-detail-actions">
              {detail.developer.role !== "superadmin" ? (
                detail.developer.status === "active" ? (
                  <button type="button" disabled={busy} onClick={() => setPendingDisable(detail.developer)}>
                    Deactivate
                  </button>
                ) : (
                  <button type="button" className="primary" disabled={busy} onClick={() => void setEnabled(detail.developer, true)}>
                    Activate
                  </button>
                )
              ) : null}
              <button type="button" disabled={busy} onClick={() => openPassword(detail.developer)}>
                Change Password
              </button>
              <button type="button" disabled={busy} onClick={() => openEdit(detail.developer)}>
                Edit
              </button>
            </div>
          </div>

          <div className="admin-tabs" role="tablist">
            {(
              [
                ["overview", "Profile"],
                ["cameras", `Cameras (${devices.length})`],
                ["keys", `API Keys (${keys.length})`],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                className={tab === id ? "primary" : ""}
                aria-selected={tab === id}
                onClick={() => setTab(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === "overview" ? (
            <div className="admin-overview">
              <div className="admin-stat-row">
                <div className="admin-stat">
                  <span className="muted">Cameras</span>
                  <strong>{detail.cameras}</strong>
                </div>
                <div className="admin-stat">
                  <span className="muted">Active API keys</span>
                  <strong>{detail.keys.active}</strong>
                </div>
                <div className="admin-stat">
                  <span className="muted">Last login</span>
                  <strong>
                    {detail.developer.last_login_at
                      ? formatKathmanduWhen(detail.developer.last_login_at)
                      : "Never"}
                  </strong>
                </div>
              </div>
              <dl className="admin-dl">
                <div>
                  <dt>Name</dt>
                  <dd>{detail.developer.name}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{detail.developer.email}</dd>
                </div>
                <div>
                  <dt>Role</dt>
                  <dd>{roleLabel(detail.developer.role)}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{statusLabel(detail.developer.status)}</dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{detail.developer.created_at ? formatKathmanduWhen(detail.developer.created_at) : "—"}</dd>
                </div>
              </dl>
            </div>
          ) : null}

          {tab === "cameras" ? (
            <div className="admin-cameras">
              <div className="admin-assign">
                <h4>Assign camera</h4>
                <p className="muted">
                  Unassigned and other users&apos; cameras can be assigned here. Ownership is stored as{" "}
                  <code>owner_developer_id</code>.
                </p>
                <div className="admin-assign-row">
                  <select value={assignDeviceId} onChange={(e) => setAssignDeviceId(e.target.value)}>
                    <option value="">Select camera…</option>
                    {assignable.map((d) => (
                      <option key={d.id} value={d.id}>
                        {cameraTitle(d)} · {d.imei}
                        {d.owner_developer_id
                          ? ` · currently ${d.owner_name || d.owner_email || "another user"}`
                          : " · unassigned"}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="primary"
                    disabled={busy || !assignDeviceId}
                    onClick={() => void assignCamera()}
                  >
                    Assign Camera
                  </button>
                </div>
                {assignable.length === 0 ? (
                  <p className="muted">No other cameras available to assign.</p>
                ) : null}
              </div>
              <ul className="admin-device-list">
                {devices.map((d) => (
                  <li key={d.id}>
                    <div>
                      <strong>{cameraTitle(d)}</strong>
                      <span className="muted">
                        {d.phone} · IMEI {d.imei}
                        {d.online != null ? ` · ${d.online ? "online" : "offline"}` : ""}
                        {d.lat != null && d.lon != null ? ` · ${d.lat.toFixed(5)}, ${d.lon.toFixed(5)}` : ""}
                      </span>
                    </div>
                    <button type="button" className="ghost" disabled={busy} onClick={() => void unassignCamera(d.id)}>
                      Unassign
                    </button>
                  </li>
                ))}
                {devices.length === 0 ? <li className="muted">No cameras assigned to this user.</li> : null}
              </ul>
            </div>
          ) : null}

          {tab === "keys" ? (
            <div className="admin-keys">
              <div className="admin-assign-row" style={{ marginBottom: 12 }}>
                <button type="button" className="primary" disabled={busy} onClick={() => void generateKey()}>
                  Generate API Key
                </button>
              </div>
              <ul className="admin-key-list">
                {keys.map((k) => (
                  <li key={k.id}>
                    <div>
                      <strong>{k.name}</strong>
                      <span className="muted">
                        {k.masked} · {k.environment} · {k.status}
                      </span>
                    </div>
                    <div className="admin-detail-actions">
                      {k.status !== "revoked" ? (
                        <>
                          <button type="button" disabled={busy} onClick={() => void keyAction(k.id, "rotate")}>
                            Rotate
                          </button>
                          {k.status === "disabled" ? (
                            <button type="button" disabled={busy} onClick={() => void keyAction(k.id, "enable")}>
                              Enable
                            </button>
                          ) : (
                            <button type="button" disabled={busy} onClick={() => void keyAction(k.id, "disable")}>
                              Disable
                            </button>
                          )}
                          <button type="button" disabled={busy} onClick={() => void keyAction(k.id, "revoke")}>
                            Revoke
                          </button>
                        </>
                      ) : null}
                    </div>
                  </li>
                ))}
                {keys.length === 0 ? <li className="muted">No API keys for this user.</li> : null}
              </ul>
            </div>
          ) : null}
        </section>
      ) : (
        <p className="muted">Select View on a user row to open details, camera assignment, and API keys.</p>
      )}

      {modal ? (
        <div className="admin-modal-backdrop" role="presentation" onClick={() => setModal(null)}>
          <div
            className="admin-modal"
            role="dialog"
            aria-labelledby="user-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="user-modal-title">
              {modal === "create" ? "Create user" : modal === "edit" ? "Edit user" : "Change password"}
            </h3>
            {error && modal ? <div className="banner admin-modal-error">{error}</div> : null}
            {modal === "create" || modal === "edit" ? (
              <>
                <label>
                  Name
                  <input value={formName} onChange={(e) => setFormName(e.target.value)} autoFocus={modal === "create"} />
                </label>
                <label>
                  Email
                  <input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} />
                </label>
                <label>
                  {modal === "create" ? "Password" : "Password (optional)"}
                  <PasswordField value={formPassword} onChange={(e) => setFormPassword(e.target.value)} autoComplete="new-password" />
                </label>
                <label>
                  Confirm password
                  <PasswordField value={formConfirm} onChange={(e) => setFormConfirm(e.target.value)} autoComplete="new-password" />
                </label>
                <p className="muted">
                  {modal === "create"
                    ? passwordRequirementText()
                    : `Leave blank to keep the current password. ${passwordRequirementText()}`}
                </p>
                <label>
                  Status
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value as "active" | "disabled")}
                    disabled={modal === "edit" && developers.find((d) => d.id === selectedId)?.role === "superadmin"}
                  >
                    <option value="disabled">Inactive</option>
                    <option value="active">Active</option>
                  </select>
                </label>
                {modal === "create" ? (
                  <p className="muted">New users default to Inactive. Activate them before they can sign in.</p>
                ) : (
                  <p className="muted">Changing status to Active and saving will persist it so the user can log in.</p>
                )}
              </>
            ) : (
              <>
                <label>
                  New password
                  <PasswordField value={formPassword} onChange={(e) => setFormPassword(e.target.value)} autoComplete="new-password" autoFocus />
                </label>
                <label>
                  Confirm password
                  <PasswordField value={formConfirm} onChange={(e) => setFormConfirm(e.target.value)} autoComplete="new-password" />
                </label>
                <p className="muted">Minimum {PASSWORD_MIN_LENGTH} characters. Existing sessions will be revoked.</p>
              </>
            )}
            <div className="admin-modal-actions">
              <button type="button" className="ghost" onClick={() => setModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                disabled={busy || (modal !== "password" && (!formName.trim() || !formEmail.trim()))}
                onClick={() => {
                  if (modal === "create") void createUser();
                  else if (modal === "edit") void saveEdit();
                  else void savePassword();
                }}
              >
                {modal === "create" ? "Create" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(pendingDisable)}
        title="Deactivate user?"
        message={
          pendingDisable
            ? `Deactivate ${pendingDisable.name}? They will not be able to log in. Cameras stay assigned.`
            : ""
        }
        confirmLabel="Deactivate"
        danger
        onCancel={() => setPendingDisable(null)}
        onConfirm={() => {
          if (pendingDisable) void setEnabled(pendingDisable, false);
        }}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete user?"
        message={
          pendingDelete
            ? `Permanently delete ${pendingDelete.name}? Their cameras will be unassigned (not deleted). Super Admin cannot be deleted.`
            : ""
        }
        confirmLabel="Delete"
        danger
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) void deleteUser(pendingDelete);
        }}
      />
    </div>
  );
}
